import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retryWhen, shareReplay, tap } from 'rxjs/operators';

import { AssetPollingReqRes } from 'src/app/dashboard/assets/AssetPollingReqRes.model';
import { AssetAndFundraisingReqRes } from 'src/app/dashboard/assets/components/create-asset/AssetAndFundraisingRequest';
import { GpAssetListItemReqRes, GpAssetReqRes } from 'src/app/dashboard/assets/GpAssetReqRes.model';
import { GpHoldingListItemReqRes } from 'src/app/dashboard/shared/holding/GpHoldingListItem.model';
import { HttpService } from 'src/app/services/http.service';
import AssetOrderBy from 'src/app/shared/enums/AssetOrderBy.enum';
import { CustomFieldsGroupNameType } from 'src/app/shared/enums/CustomFieldsType.enum';
import HoldingDiscriminator from 'src/app/shared/enums/HoldingDiscriminator.enum';
import HoldingStatus from 'src/app/shared/enums/HoldingStatus.enum';
import HTTP_STATUS_CODES from 'src/app/shared/enums/HttpStatusCodesEnum';
import { GpAssetOverviewItemReqRes } from '../../dashboard/assets/GpAssetOverviewItemReqRes';
import { IdValue } from '../../shared/models/IdValue.model';
import { RoutingService } from '../routing.service';

@Injectable()
export class GpAssetDataService {
  private readonly ASSET_BASE_ENDPOINT = 'gp/assets';
  private readonly CREATE_ASSET_ENDPOINT = this.ASSET_BASE_ENDPOINT;
  private readonly discriminator = HoldingDiscriminator.Asset;

  private readonly http: HttpService = inject(HttpService);
  private readonly router: Router = inject(Router);
  private readonly routingService: RoutingService = inject(RoutingService);

  getAssetsList(orderBy: AssetOrderBy = AssetOrderBy.CreatedDateNewerFirst): Observable<GpAssetListItemReqRes[]> {
    return this.http.getTyped<GpAssetListItemReqRes[]>(`${this.ASSET_BASE_ENDPOINT}?orderBy=${orderBy}`).pipe(
      map(assets => assets.map<GpAssetListItemReqRes>(asset =>
        ({...asset, discriminator: this.discriminator}))),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  getDealsList(orderBy: AssetOrderBy = AssetOrderBy.NameAsc): Observable<GpAssetOverviewItemReqRes[]> {
    return this.http.getTyped<GpAssetOverviewItemReqRes[]>(`${this.ASSET_BASE_ENDPOINT}/overview?orderBy=${orderBy}`).pipe(
      map(assets => assets?.map<GpAssetOverviewItemReqRes>(asset =>
        ({...asset, discriminator: this.discriminator}))),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  getDealsBalances(): Observable<IdValue<number>[]> {
    return this.http.getTyped<IdValue<number>[]>(`${this.ASSET_BASE_ENDPOINT}/overview/balances`).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  detectChanges(assetId: number, fundraisingId: number, distributionId: number, changedSince: Date): Observable<AssetPollingReqRes> {
    const pollRequest = {
      fundraisingId,
      distributionId,
      changedSince
    };

    return this.http
      .postTyped<AssetPollingReqRes>(`${this.ASSET_BASE_ENDPOINT}/${assetId}/detectChanges`, pollRequest)
      .pipe(
        map(response => {
          return response ? ({
          ...response,
          assetDetails: this.addDiscriminatorToAsset(response.assetDetails)
        }) : null}),
        retryWhen(error =>
          error.pipe(
            tap(err => {
              if (err.status !== 0) { // When Azure app service reaches timeout (230 seconds) error received with 0 status
                throw error;
              }
            })
          )),
        catchError(err => {
          // if (err.status === 0) {
          //   console.log('detect changes request has failed');
          //   return this.detectChanges(assetId, fundraisingId, changedSince);
          // }
          // else
          if (err.status === HTTP_STATUS_CODES.NOT_FOUND) {
            // if asset polling (initial fundraising): return to assets list
            if (fundraisingId === 0) {
              this.router.navigateByUrl(this.routingService.gpAssetsPage);
            } else {
              this.router.navigateByUrl(this.routingService.gpAssetPage(assetId));
            }
          }
          return throwError(() => err);
        })
      );
  }

  getAssetById(assetId: number): Observable<GpAssetReqRes> {
    const url = `${this.ASSET_BASE_ENDPOINT}/${assetId}`;
    return this.http.getTyped<GpAssetReqRes>(url).pipe(
      map(asset => this.addDiscriminatorToAsset(asset)),
      shareReplay());
  }

  createAsset(model: AssetAndFundraisingReqRes): Observable<AssetAndFundraisingReqRes> {
    return this.http.postTyped<AssetAndFundraisingReqRes>(this.CREATE_ASSET_ENDPOINT, model).pipe(
      map(response => this.addDiscriminatorToAssetAndFundraising(response)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  createFundAsset(model: GpAssetReqRes): Observable<GpAssetReqRes> {
    return this.http.postTyped<GpAssetReqRes>(`${this.CREATE_ASSET_ENDPOINT}/fund-asset`, model).pipe(
      map(asset => this.addDiscriminatorToAsset(asset)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  createDraftAsset(model: GpAssetReqRes): Observable<GpAssetReqRes> {
    return this.http.postTyped<GpAssetReqRes>(`${this.CREATE_ASSET_ENDPOINT}/draft`, model).pipe(
      map(asset => this.addDiscriminatorToAsset(asset)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  updateAsset(assetId: number, model: AssetAndFundraisingReqRes, skipNotification = false): Observable<AssetAndFundraisingReqRes> {
    return this.http.putTyped<AssetAndFundraisingReqRes>(`${this.ASSET_BASE_ENDPOINT}/${assetId}?skipNotification=${skipNotification}`, model).pipe(
      map(response => this.addDiscriminatorToAssetAndFundraising(response)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  updateFundAsset(assetId: number, model: GpAssetReqRes, skipNotification = false): Observable<GpAssetReqRes> {
    return this.http.putTyped<GpAssetReqRes>(`${this.ASSET_BASE_ENDPOINT}/fund-asset/${assetId}?skipNotification=${skipNotification}`, model).pipe(
      map(asset => this.addDiscriminatorToAsset(asset)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  updateAssetVisibility(model: GpAssetOverviewItemReqRes): Observable<boolean> {
    return this.http.putTyped<boolean>(`${this.ASSET_BASE_ENDPOINT}/visibility`, model).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  updateDraftAsset(assetId: number, model: GpAssetReqRes): Observable<void> {
    return this.http.putTyped<void>(`${this.ASSET_BASE_ENDPOINT}/draft/${assetId}`, model).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  /** Create the initial fundraising and move the asset to fundraising status */
  moveAssetToFundraising(assetId: number, model: AssetAndFundraisingReqRes): Observable<AssetAndFundraisingReqRes> {
    return this.http.postTyped<AssetAndFundraisingReqRes>(`${this.ASSET_BASE_ENDPOINT}/${assetId}/move-to-fundraising`, model).pipe(
      map(response => this.addDiscriminatorToAssetAndFundraising(response)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  moveAssetToUnderManagement(assetId: number, model: AssetAndFundraisingReqRes): Observable<AssetAndFundraisingReqRes> {
    return this.http.patchTyped<AssetAndFundraisingReqRes>(`${this.ASSET_BASE_ENDPOINT}/${assetId}/move-to-under-management`, model).pipe(
      map(response => this.addDiscriminatorToAssetAndFundraising(response)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  moveFundraisingToCompleted(assetId: number, model: AssetAndFundraisingReqRes): Observable<AssetAndFundraisingReqRes> {
    return this.http.patchTyped<AssetAndFundraisingReqRes>(`${this.ASSET_BASE_ENDPOINT}/${assetId}/move-fundraising-to-completed`, model).pipe(
      map(response => this.addDiscriminatorToAssetAndFundraising(response)),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  updateStatus(assetId: number, holdingStatus: HoldingStatus, draftSendEmailsWhenMovingToUnderManagement = false) {
    const model = new GpAssetReqRes();
    model.id = assetId;
    model.status = holdingStatus;
    return this.http.putTyped<GpAssetReqRes>(
      `${this.ASSET_BASE_ENDPOINT}/${assetId}/status?sendEmails=${draftSendEmailsWhenMovingToUnderManagement}`, model)
      .pipe(
        map(asset => this.addDiscriminatorToAsset(asset)),
        catchError(err => {
          return throwError(() => err);
        })
      );
  }

  deleteAsset(assetId: number) {
    return this.http.deleteTyped<void>(`${this.ASSET_BASE_ENDPOINT}/${assetId}`).pipe(
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  isAssetNameExists(assetName: string, assetIdToExclude: number = 0) {
    assetName = assetName.trim();
    const url = `${this.ASSET_BASE_ENDPOINT}/asset-name-exists-check`;
    return this.http.postTyped<boolean>(url, {assetName, assetIdToExclude}).pipe(shareReplay(1));
  }

  getCustomFieldsSuggestions(groupNames: Array<CustomFieldsGroupNameType>): Observable<{[key in CustomFieldsGroupNameType]: Array<{name: string}>}> { 
    return this.http.getTyped<any>(`custom-fields/name-suggestions?entityType=Asset&groupNames=${groupNames.join(',')}`).pipe(
      map(response => response?.groupSuggestions),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  private addDiscriminatorToAsset(asset: GpHoldingListItemReqRes) {
    return {...asset, discriminator: this.discriminator} as GpAssetReqRes;
  }

  private addDiscriminatorToAssetAndFundraising(assetAndFundraising: AssetAndFundraisingReqRes) {
    return {
      ...assetAndFundraising,
      asset: this.addDiscriminatorToAsset(assetAndFundraising.asset)
    } as AssetAndFundraisingReqRes;
  }
}
